import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdInputGroup,
  CmdLabel,
  CmdRadioButton,
  CmdSimpleSelect,
  CmdSimpleSelectOption,
  CmdSubpage,
  CmdSubpageButton,
  CmdSwitch,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useRecoilValue } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import ApiService from '../../Services/ApiService';
import PermissionsService, { PERMISSION_PERIOD_LOCK } from '../../Services/PermissionsService';
import ConfirmDialog from '../ConfirmDialog';
import usePageWithForm from '../../hooks/usePageWithForm';
import OnlineHelp from '../OnlineHelp';
import { transformErrors } from '../../Services/HookFormService';
import { customersAtom, ICustomer } from '../../store/recoil/customers';
import useAnalytics from '../../hooks/useAnalytics';

const RESPONSE_CONFLICT = 'Conflict';

interface FormProps {
  isEdited: boolean;
  closeCallback: () => void;
  editingItemId: number | null;
  updateTable?: (() => void) | undefined;
  customerIds?: number | null;
  customerOptions: CmdSimpleSelectOption[] | null;
}

interface IPeriodLockInputForm {
  customerId: string;
  lockTo: string;
  lockAfterCustom: { [key: number]: number | undefined };
  lockAfterType: string;
  lockAfterDefault: number;
  enabled: string;
}

function Form(props: FormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();

  const [loader, setLoader] = React.useState<boolean>(false);
  const [dataReadyToSubmit, setDataReadyToSubmit] = React.useState<any | null>(null);
  const [isCancelButtonClicked, setIsCancelButtonClicked] = React.useState<boolean>(false);
  const [lockAfterDefaultValue, setLockAfterDefaultValue] = React.useState<number>(0);
  const [selectedCustomer, setSelectedCustomer] = React.useState<number | null>(null);
  const [selectedLockTo, setSelelectedLockTo] = React.useState<number | null>(null);
  const customers = useRecoilValue<ICustomer[]>(customersAtom);
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [lockAfterCustomOptions, setLockAfterCustomOptions] = React.useState<
    CmdSimpleSelectOption[]
  >([]);
  const { trackEvent } = useAnalytics();

  const customerOptions = props.customerOptions;

  const lockToOptions = [
    { item: f({ id: 'periodLock.firstDay' }), value: '1' },
    { item: `${f({ id: 'periodLock.to' })} 2. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '2' },
    { item: `${f({ id: 'periodLock.to' })} 3. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '3' },
    { item: `${f({ id: 'periodLock.to' })} 4. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '4' },
    { item: `${f({ id: 'periodLock.to' })} 5. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '5' },
    { item: `${f({ id: 'periodLock.to' })} 6. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '6' },
    { item: `${f({ id: 'periodLock.to' })} 7. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '7' },
    { item: `${f({ id: 'periodLock.to' })} 8. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '8' },
    { item: `${f({ id: 'periodLock.to' })} 9. ${f({ id: 'periodLock.dayOfMonth' })}`, value: '9' },
    {
      item: `${f({ id: 'periodLock.to' })} 10. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '10',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 11. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '11',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 12. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '12',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 13. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '13',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 14. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '14',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 15. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '15',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 16. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '16',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 17. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '17',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 18. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '18',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 19. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '19',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 20. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '20',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 21. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '21',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 22. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '22',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 23. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '23',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 24. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '24',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 25. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '25',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 26. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '26',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 27. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '27',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 28. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '28',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 29. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '29',
    },
    {
      item: `${f({ id: 'periodLock.to' })} 30. ${f({ id: 'periodLock.dayOfMonth' })}`,
      value: '30',
    },
    { item: f({ id: 'periodLock.lastDay' }), value: '31' },
  ];

  const validateFields = (values) => {
    let formErrors: any = {};
    if (validation) {
      formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
      if (values.lockAfterType === 'custom') {
        if (Object.values(values.lockAfterCustom).length !== 12) {
          const errorKeys: string[] = [];
          const allKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
          const lockAfterCustomKeys = Object.keys(values.lockAfterCustom);
          allKeys.forEach((key: string) => {
            if (!lockAfterCustomKeys.includes(key)) {
              errorKeys.push(String(key));
            }
          });
          const errorKeysMessage = `errorKeys:${errorKeys}<br />`;
          formErrors.lockAfterCustom = formErrors.lockAfterCustom
            ? `${formErrors.lockAfterCustom}${errorKeysMessage}${f({
                id: 'periodLock.lockAfterCustom.required',
              })}`
            : `${errorKeysMessage}${f({ id: 'periodLock.lockAfterCustom.required' })}`;
        }
        delete formErrors.lockAfterDefault;
      } else if (values.lockAfterType === 'default') {
        if (!values.lockAfterDefault && values.lockAfterDefault !== 0) {
          formErrors.lockAfterDefault = formErrors.lockAfterDefault
            ? `${formErrors.lockAfterDefault}${f({ id: 'waypoints.edit.title.required' })}`
            : f({ id: 'waypoints.edit.title.required' });
        }
      }
    }
    if (customerOptions?.length === 1) {
      delete formErrors.customerId;
    }
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm<IPeriodLockInputForm>({
    mode: 'onChange',
    defaultValues: {
      customerId: '',
      lockTo: '31',
      lockAfterCustom: {
        0: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        6: 0,
        7: 0,
        8: 0,
        9: 0,
        10: 0,
        11: 0,
      },
      lockAfterType: 'default',
      lockAfterDefault: '',
      enabled: '1',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleConfirmKeepSubpage = () => {
    setIsCancelButtonClicked(false);
    trackEvent([`period lock definition form`, 'click', 'Cancel Subpage']);
  };

  const handleClickOnSubmit = (values) => {
    setDataReadyToSubmit(values);
    trackEvent([`period lock definition form`, 'click', 'Submit']);
  };

  const handleConfirmCancelAction = () => {
    setDataReadyToSubmit(null);
    trackEvent([`period lock definition form`, 'click', 'Cancel action']);
  };

  const handleCustomValidation = (lockAfterType: string) => {
    const keyWithValidation = lockAfterType === 'custom' ? 'lockAfterCustom' : 'lockAfterDefault';
    const keyWithoutValidation =
      lockAfterType === 'custom' ? 'lockAfterDefault' : 'lockAfterCustom';
    setValidation((oldState) => ({
      ...oldState,
      [keyWithValidation]: [
        {
          param: 'min',
          value: 0,
          message: `${f({ id: 'alarms.modal.rule.min.text.default' })}: 0`,
        },
        {
          param: 'max',
          value: 90,
          message: `${f({ id: 'alarms.modal.rule.max.text.default' })}: 90`,
        },
      ],
      [keyWithoutValidation]: [],
    }));
  };

  React.useEffect(() => {
    const lockAfterType = watch('lockAfterType');
    if (lockAfterType) {
      const keyWithValidation = lockAfterType === 'custom' ? 'lockAfterCustom' : 'lockAfterDefault';
      const keyWithoutValidation =
        lockAfterType === 'custom' ? 'lockAfterDefault' : 'lockAfterCustom';
      setError(keyWithoutValidation, { message: '' });
      trigger(keyWithValidation);
      trigger(keyWithoutValidation);
    }
  }, [setError, trigger, validation, watch]);

  const handleOnSubmit = async (values) => {
    trackEvent([`period lock definition form`, 'click', 'Submit form']);
    const newValues = {
      ...values,
    };
    if (!values.customerId) {
      newValues.customerId =
        customerOptions && customerOptions[0] ? customerOptions[0].value : null;
    }
    if (values.lockAfterType === 'custom') {
      newValues.lockAfterDefault = null;
      const lockAfterCustomArray: number[] = [];
      Object.values(values.lockAfterCustom).forEach((i: any) => {
        lockAfterCustomArray.push(Number(i));
      });
      newValues.lockAfterCustom = lockAfterCustomArray;
    } else if (values.lockAfterType === 'default') {
      newValues.lockAfterCustom = null;
      newValues.lockAfterDefault = Number(values.lockAfterDefault);
    }
    newValues.customerId = Number(newValues.customerId);
    newValues.lockTo = Number(values.lockTo);
    newValues.enabled = String(values.enabled) === '1';

    setLoader(true);

    const response = props.editingItemId
      ? await ApiService.put(`/v1/period-lock-definition/${props.editingItemId}/edit`, newValues)
      : await ApiService.post(`/v1/period-lock-definition/add`, newValues);
    setLoader(false);

    if (
      response &&
      response.data &&
      (response.status === 200 || response.statusText === RESPONSE_CONFLICT) &&
      props.updateTable
    ) {
      props.closeCallback();
      props.updateTable();
    }
  };

  const handleConfirmSubmitAction = () => {
    if (dataReadyToSubmit) {
      handleOnSubmit(dataReadyToSubmit);
    }
  };

  const handleConfirmCancelSubpage = () => {
    props.closeCallback();
    trackEvent([`period lock definition form`, 'click', 'Cancel confirm dialog']);
  };

  const closeSubpage = () => {
    setIsCancelButtonClicked(true);
    trackEvent([`period lock definition form`, 'click', 'Close subpage']);
  };

  const getValidation = async () => {
    const url = `/v1/period-lock-definition/validation`;
    setLoader(true);
    const response = await ApiService.get(url);

    if (response.data && response.data.validation && response.status === 200) {
      if (props.editingItemId) {
        setValidation({ ...response.data.validation, lockAfterDefault: [], lockAfterCustom: [] });
      } else {
        setValidation({
          ...response.data.validation,
          lockAfterDefault: [
            {
              param: 'min',
              value: 0,
              message: `${f({ id: 'alarms.modal.rule.min.text.default' })}: 0`,
            },
            {
              param: 'max',
              value: 90,
              message: `${f({ id: 'alarms.modal.rule.max.text.default' })}: 90`,
            },
          ],
          lockAfterCustom: [],
        });
      }
    }
    setLoader(false);
  };

  const getLockToLabel = () => {
    const options = lockToOptions.find((i: any) => String(i.value) === String(selectedLockTo));
    return options ? options.item : '';
  };

  const getCustomerLabel = () => {
    const customer = customers.find((item: ICustomer) => item.id === selectedCustomer);
    if (customer) {
      return customer.name;
    }
    return '';
  };

  const getDateNumber = (val: string) => {
    const value = Number(val) + 1;
    if (value < 10) {
      return `0${value}`;
    }
    return String(value);
  };

  const handlePeriodsLables = (value: string) => {
    let newOptions: CmdSimpleSelectOption[] = [];
    if (String(value) === '31') {
      newOptions = [
        { item: '01.01. - 31.01.', value: '0' },
        { item: '01.02. - 28.02.', value: '1' },
        { item: '01.03. - 31.03.', value: '2' },
        { item: '01.04. - 30.04.', value: '3' },
        { item: '01.05. - 31.05.', value: '4' },
        { item: '01.06. - 30.06.', value: '5' },
        { item: '01.07. - 31.07.', value: '6' },
        { item: '01.08. - 31.08.', value: '7' },
        { item: '01.09. - 30.09.', value: '8' },
        { item: '01.10. - 31.10.', value: '9' },
        { item: '01.11. - 30.11.', value: '10' },
        { item: '01.12. - 31.12.', value: '11' },
      ];
    } else if (String(value) === '30') {
      newOptions = [
        { item: '31.01. - 28.02.', value: '0' },
        { item: '01.03. - 30.03.', value: '1' },
        { item: '31.03. - 30.04.', value: '2' },
        { item: '01.05. - 30.05.', value: '3' },
        { item: '31.05. - 30.06.', value: '4' },
        { item: '01.07. - 30.07.', value: '5' },
        { item: '31.07. - 30.08.', value: '6' },
        { item: '31.08. - 30.09.', value: '7' },
        { item: '01.10. - 30.10.', value: '8' },
        { item: '31.10. - 30.11.', value: '9' },
        { item: '01.12. - 30.12.', value: '10' },
        { item: '31.12. - 30.01.', value: '11' },
      ];
    } else {
      if (Number(value) < 10) {
        value = `0${value}`;
      }
      newOptions = [
        {
          item: `${getDateNumber(value)}.01. - ${Number(value) > 28 ? '28' : value}.02.`,
          value: '0',
        },
        {
          item: `${Number(value) > 28 ? '01.03' : `${getDateNumber(value)}.02.`} - ${value}.03.`,
          value: '1',
        },
        {
          item: `${getDateNumber(value)}.03. - ${Number(value) > 30 ? '30' : value}.04.`,
          value: '2',
        },
        {
          item: `${Number(value) > 30 ? '1' : getDateNumber(value)}.04. - ${value}.05.`,
          value: '3',
        },
        {
          item: `${getDateNumber(value)}.05. - ${Number(value) > 30 ? '30' : value}.06.`,
          value: '4',
        },
        {
          item: `${Number(value) > 30 ? '1' : getDateNumber(value)}.06. - ${value}.07.`,
          value: '5',
        },
        { item: `${getDateNumber(value)}.07. - ${value}.08.`, value: '6' },
        {
          item: `${getDateNumber(value)}.08. - ${Number(value) > 30 ? '30' : value}.09.`,
          value: '7',
        },
        {
          item: `${Number(value) > 30 ? '1' : getDateNumber(value)}.09. - ${value}.10.`,
          value: '8',
        },
        {
          item: `${getDateNumber(value)}.10. - ${Number(value) > 30 ? '30' : value}.11.`,
          value: '9',
        },
        {
          item: `${Number(value) > 30 ? '30' : getDateNumber(value)}.11. - ${value}.12.`,
          value: '10',
        },
        { item: `${getDateNumber(value)}.12. - ${value}.01.`, value: '11' },
      ];
    }
    setLockAfterCustomOptions(newOptions);
  };

  const getData = async () => {
    const url = `/v1/period-lock-definition/${props.editingItemId}`;
    setLoader(true);

    const response = await ApiService.get(url);

    if (
      response &&
      response.data &&
      response.status === 200 &&
      response.data.periodLockDefinition
    ) {
      const fields = response.data.periodLockDefinition;

      setValue('customerId', fields.customerId);
      setSelectedCustomer(fields.customerId);
      setValue('lockTo', fields.lockTo);
      setSelelectedLockTo(fields.lockTo);
      if (fields.lockAfterCustom) {
        const lockAfterCustomObj: { [key: number]: number } = {};
        fields.lockAfterCustom.forEach((i: number, key: number) => {
          lockAfterCustomObj[key] = Number(i);
        });
        setValue('lockAfterCustom', lockAfterCustomObj);
        // setLockAfterCustomValue(lockAfterCustomObj);
      } else if (fields.lockAfterDefault) {
        const lockAfterDefault = fields.lockAfterDefault;
        const lockAfterCustom = lockAfterDefault
          ? {
              0: lockAfterDefault,
              1: lockAfterDefault,
              2: lockAfterDefault,
              3: lockAfterDefault,
              4: lockAfterDefault,
              5: lockAfterDefault,
              6: lockAfterDefault,
              7: lockAfterDefault,
              8: lockAfterDefault,
              9: lockAfterDefault,
              10: lockAfterDefault,
              11: lockAfterDefault,
            }
          : {};
        setValue('lockAfterCustom', lockAfterCustom);
        // setLockAfterCustomValue(lockAfterCustom);
      }
      setValue('lockAfterDefault', fields.lockAfterDefault);
      setValue('lockAfterType', fields.lockAfterType);
      setValue('enabled', fields.enabled === true ? '1' : '0');
      handleCustomValidation(fields.lockAfterType);
    }

    setLoader(false);
  };

  React.useEffect(() => {
    getValidation();
    if (props.editingItemId) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subpageButtons: CmdSubpageButton[] =
    (props.editingItemId &&
      customerOptions &&
      PermissionsService.hasAccess(
        PERMISSION_PERIOD_LOCK,
        ['update'],
        Number(watch('customerId'))
      )) ||
    !props.editingItemId
      ? [
          {
            id: 'supplier-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: closeSubpage,
          },
          {
            id: 'periodLock.submit',
            type: 'submit',
            className: 'e-button e-button--gray',
            title: f({ id: 'suppliersForm.save' }),
            submitCallback: handleSubmit(handleClickOnSubmit),
          },
        ]
      : [
          {
            id: 'supplier-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: closeSubpage,
          },
        ];

  React.useEffect(() => {
    handlePeriodsLables(watch('lockTo'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('lockTo')]);

  React.useEffect(() => {
    if (watch('lockAfterType') === 'default') {
      const lockAfterDefault = watch('lockAfterDefault');
      const lockAfterCustom =
        lockAfterDefault !== null &&
        lockAfterDefault !== undefined &&
        String(lockAfterDefault) !== ''
          ? {
              0: lockAfterDefault,
              1: lockAfterDefault,
              2: lockAfterDefault,
              3: lockAfterDefault,
              4: lockAfterDefault,
              5: lockAfterDefault,
              6: lockAfterDefault,
              7: lockAfterDefault,
              8: lockAfterDefault,
              9: lockAfterDefault,
              10: lockAfterDefault,
              11: lockAfterDefault,
            }
          : {};
      setValue('lockAfterCustom', lockAfterCustom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockAfterDefaultValue, watch('lockAfterType')]);

  return (
    <CmdSubpage
      id="supplier-form"
      title={f({ id: 'menu.periodLock' })}
      subtitle={
        props.isEdited ? f({ id: 'periodLock.editSetting' }) : f({ id: 'periodLock.addSetting' })
      }
      buttons={subpageButtons}
      loader={loader}
      maintitle={f({ id: 'menu.settings' })}
      onlineHelp={<OnlineHelp topic="period-lock" />}
    >
      <div>
        <div className="row">
          <div className="col-xxl-6">
            <CmdBox id="alarms-basic-information" title={f({ id: 'periodLock.periodSettings' })}>
              {props.editingItemId ? (
                [
                  <CmdLabel
                    title={f({ id: 'periodLock.customerId' })}
                    value={getCustomerLabel()}
                  />,
                  <Controller
                    name="enabled"
                    control={control}
                    rules={{
                      required: errors.enabled ? errors.enabled.message : undefined,
                    }}
                    render={({ field }) => (
                      <CmdSwitch
                        {...field}
                        id="enabled"
                        label={f({ id: 'alarms.section.basicInformation.enabled' })}
                        disabled={
                          !PermissionsService.hasAccess(
                            PERMISSION_PERIOD_LOCK,
                            ['update'],
                            Number(watch('customerId'))
                          )
                        }
                        value={watch('enabled')}
                        setValue={(value) => {
                          setValue('enabled', value);
                          trigger('enabled');
                        }}
                        tooltip={f({ id: 'periodLock.enabled.tooltip' })}
                      />
                    )}
                  />,
                  <CmdLabel title={f({ id: 'periodLock.lockTo' })} value={getLockToLabel()} />,
                ]
              ) : (
                <>
                  {customerOptions && customerOptions.length > 1 && (
                    <Controller
                      name="customerId"
                      control={control}
                      rules={{
                        required: errors.customerId ? errors.customerId.message : undefined,
                      }}
                      render={({ field }) => (
                        <CmdSimpleSelect
                          {...field}
                          id="customerId"
                          name="customerId"
                          label={f({ id: 'periodLock.customerId' })}
                          options={props.customerOptions || []}
                          introOption={f({ id: 'suppliersForm.choose' })}
                          required
                          onChangeCallback={(value: string) => {
                            setValue('customerId', value);
                          }}
                          touched
                          error={errors.customerId?.message}
                        />
                      )}
                    />
                  )}
                  <Controller
                    name="enabled"
                    control={control}
                    rules={{
                      required: errors.enabled ? errors.enabled.message : undefined,
                    }}
                    render={({ field }) => (
                      <CmdSwitch
                        {...field}
                        id="enabled"
                        label={f({ id: 'alarms.section.basicInformation.enabled' })}
                        disabled={
                          !PermissionsService.hasAccess(
                            PERMISSION_PERIOD_LOCK,
                            ['update'],
                            Number(watch('customerId'))
                          )
                        }
                        value={watch('enabled')}
                        setValue={(value: string) => {
                          setValue('enabled', value);
                          trigger('enabled');
                        }}
                        tooltip={f({ id: 'periodLock.enabled.tooltip' })}
                      />
                    )}
                  />
                  <Controller
                    name="lockTo"
                    control={control}
                    rules={{
                      required: errors.lockTo ? errors.lockTo.message : undefined,
                    }}
                    render={({ field }) => (
                      <CmdSimpleSelect
                        {...field}
                        id="lockTo"
                        name="lockTo"
                        label={f({ id: 'periodLock.lockTo' })}
                        options={lockToOptions}
                        value={watch('lockTo')}
                        introOption={f({ id: 'suppliersForm.choose' })}
                        note={`(${f({ id: 'periodLock.lockTo.note' })})`}
                        tooltip={f({ id: 'periodLock.lockTo.tooltip' })}
                        required
                        onChangeCallback={(value: string) => {
                          setValue('lockTo', value);
                        }}
                        touched
                        error={errors.lockTo?.message}
                      />
                    )}
                  />
                </>
              )}
              <Controller
                name="lockAfterType"
                control={control}
                render={({ field }) => (
                  <CmdRadioButton
                    {...field}
                    id="lockAfterType"
                    name="lockAfterType"
                    label={f({ id: 'periodLock.lockAfterType' })}
                    value={watch('lockAfterType')}
                    options={[
                      { item: f({ id: 'periodLock.default' }), value: 'default' },
                      { item: f({ id: 'periodLock.custom' }), value: 'custom' },
                    ]}
                    required={true}
                    setValue={(value: string) => {
                      handleCustomValidation(value);
                      setValue('lockAfterType', value);
                    }}
                    disabled={
                      !PermissionsService.hasAccess(
                        PERMISSION_PERIOD_LOCK,
                        ['update'],
                        Number(watch('customerId'))
                      )
                    }
                  />
                )}
              />
              {watch('lockAfterType') === 'default' && (
                <Controller
                  name="lockAfterDefault"
                  control={control}
                  rules={{
                    required: errors.lockAfterDefault ? errors.lockAfterDefault.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      id="lockAfterDefault"
                      name="lockAfterDefault"
                      type="number"
                      label={f({ id: 'periodLock.lockAfterDefault' })}
                      value={watch('lockAfterDefault')}
                      placeholder={f({ id: 'periodLock.lockAfterDefault.placeholder' })}
                      error={errors.lockAfterDefault?.message}
                      touched={true}
                      required={watch('lockAfterType') === 'default'}
                      disabled={
                        !PermissionsService.hasAccess(
                          PERMISSION_PERIOD_LOCK,
                          ['update'],
                          Number(watch('customerId'))
                        ) || watch('lockAfterType') !== 'default'
                      }
                      tooltip={f({ id: 'periodLock.lockAfterDefault.tooltip' })}
                      setValue={(value: number) => {
                        setValue('lockAfterDefault', value);
                        trigger('lockAfterDefault');
                        setLockAfterDefaultValue(value);
                      }}
                      min={0}
                      noDecimal={true}
                    />
                  )}
                />
              )}
            </CmdBox>
          </div>

          <div className="col-xxl-6">
            <CmdBox id="reports-sending-settings" title={f({ id: 'periodLock.periods' })}>
              <Controller
                name="lockAfterCustom"
                control={control}
                rules={{
                  required: errors.lockAfterCustom ? errors.lockAfterCustom.message : undefined,
                }}
                render={({ field }) => (
                  <CmdInputGroup
                    {...field}
                    id="lockAfterCustom"
                    name="lockAfterCustom"
                    label={
                      watch('lockAfterType') === 'custom'
                        ? f({ id: 'periodLock.lockAfterCustom' })
                        : ''
                    }
                    options={lockAfterCustomOptions}
                    value={watch('lockAfterCustom')}
                    error={errors.lockAfterCustom?.message}
                    touched={true}
                    required={watch('lockAfterType') === 'custom'}
                    disabled={
                      !PermissionsService.hasAccess(
                        PERMISSION_PERIOD_LOCK,
                        ['update'],
                        Number(watch('customerId'))
                      ) || watch('lockAfterType') !== 'custom'
                    }
                    type="number"
                    setValue={(value) => {
                      setValue('lockAfterCustom', value);
                      trigger('lockAfterCustom');
                    }}
                    noDecimal={true}
                  />
                )}
              />
            </CmdBox>
          </div>
        </div>
      </div>
      {dataReadyToSubmit && (
        <ConfirmDialog
          title={f({ id: 'periodLock.saveRecord' })}
          cancelCallback={handleConfirmCancelAction}
          continueCallback={handleConfirmSubmitAction}
          cancelButtonTitle={f({ id: 'suppliersForm.close' })}
          continueButtonTitle={f({ id: 'suppliersForm.save' })}
        >
          {f({ id: `periodLock.${!props.editingItemId ? 'submit' : 'edit'}Info` })}
          <br />
          <br />
          {f({ id: `periodLock.${!props.editingItemId ? 'submit' : 'edit'}Question` })}
        </ConfirmDialog>
      )}
      {isCancelButtonClicked && (
        <ConfirmDialog
          title={f({ id: 'table.confirm.dialog.onlineTemplates.subpage.close.title' })}
          cancelCallback={handleConfirmKeepSubpage}
          continueCallback={handleConfirmCancelSubpage}
          cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
          continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
        >
          {f({ id: 'table.confirm.dialog.onlineTemplates.subpage.close.message' })}
        </ConfirmDialog>
      )}
    </CmdSubpage>
  );
}

export default Form;
